// A computed colour as hex. The probe the section resolves each token
// through reads back as `rgb(…)`/`rgba(…)` or, for a colour mixed with
// `color-mix(in srgb, …)`, as `color(srgb r g b)` with channels from 0 to 1;
// the section shows every value as `#rrggbb`, or `#rrggbbaa` when the colour
// is not opaque. Anything else (empty, a keyword, another colour space) is
// returned unchanged.

const byte = (n: number): string => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0');

// An alpha as a number from 0 to 1: "0.5" or "50%".
const alpha = (text: string | undefined): number | undefined => {
  if (text === undefined) return 1;
  const n = parseFloat(text);
  if (Number.isNaN(n)) return undefined;
  return text.trim().endsWith('%') ? n / 100 : n;
};

const hex = (rgb: number[], a: number): string => `#${rgb.map(byte).join('')}${a < 1 ? byte(a * 255) : ''}`;

export function toHex(color: string): string {
  const text = color.trim();
  let m = /^rgba?\(\s*([^)]*)\)$/i.exec(text);
  if (m) {
    // "r, g, b[, a]" or "r g b[ / a]"; channels 0–255 or percentages.
    const [channels, slashAlpha] = m[1].split('/');
    const parts = channels.split(/[\s,]+/).filter(Boolean);
    if (parts.length < 3 || parts.length > 4 || (parts.length === 4 && slashAlpha !== undefined)) return color;
    const rgb = parts.slice(0, 3).map((p) => (p.endsWith('%') ? (parseFloat(p) * 255) / 100 : parseFloat(p)));
    const a = alpha(slashAlpha ?? parts[3]);
    if (rgb.some(Number.isNaN) || a === undefined) return color;
    return hex(rgb, a);
  }
  m = /^color\(\s*srgb\s+([^)]*)\)$/i.exec(text);
  if (m) {
    // "r g b[ / a]"; channels 0–1 or percentages.
    const [channels, slashAlpha] = m[1].split('/');
    const parts = channels.trim().split(/\s+/);
    if (parts.length !== 3) return color;
    const rgb = parts.map((p) => (p.endsWith('%') ? (parseFloat(p) * 255) / 100 : parseFloat(p) * 255));
    const a = alpha(slashAlpha);
    if (rgb.some(Number.isNaN) || a === undefined) return color;
    return hex(rgb, a);
  }
  return color;
}
